import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FileText,
  Download,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Users,
  BarChart3,
  Trophy,
  Target,
  TrendingUp,
  FileDown,
  Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

interface ReportGenerationDashboardProps {
  playerId?: string;
  teamId?: string;
}

const ReportGenerationDashboard: React.FC<ReportGenerationDashboardProps> = ({
  playerId,
  teamId: _teamId,
}) => {
  const { toast } = useToast();
  const [selectedReportType, setSelectedReportType] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [selectedPlayers] = useState<string[]>([]);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [, setShowPreview] = useState(false);

  const reportTypes = [
    {
      id: 'daily_session',
      name: 'Daily Session Summary',
      description: 'Detailed analysis of today\'s training session',
      icon: FileText,
      color: 'bg-blue-500',
      duration: '2-3 min',
      requiresSession: true,
    },
    {
      id: 'weekly_progress',
      name: 'Weekly Progress Report',
      description: 'Week-over-week performance trends and improvements',
      icon: TrendingUp,
      color: 'bg-green-500',
      duration: '3-5 min',
      requiresPlayer: true,
    },
    {
      id: 'monthly_assessment',
      name: 'Monthly Performance Assessment',
      description: 'Comprehensive monthly review with goals and achievements',
      icon: Trophy,
      color: 'bg-purple-500',
      duration: '5-7 min',
      requiresPlayer: true,
    },
    {
      id: 'parent_update',
      name: 'Parent Update Report',
      description: 'Age-appropriate progress update for parents',
      icon: Users,
      color: 'bg-orange-500',
      duration: '2-3 min',
      requiresPlayer: true,
    },
    {
      id: 'technique_guide',
      name: 'Technique Improvement Guide',
      description: 'Personalized technique analysis with drills',
      icon: Target,
      color: 'bg-red-500',
      duration: '4-6 min',
      requiresPlayer: true,
    },
    {
      id: 'team_overview',
      name: 'Team Performance Overview',
      description: 'Comparative analysis of team members',
      icon: BarChart3,
      color: 'bg-indigo-500',
      duration: '6-8 min',
      requiresTeam: true,
    },
  ];

  const handleGenerateReport = async () => {
    if (!selectedReportType) {
      toast({
        title: 'Select a report type',
        description: 'Please choose a report type to generate',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setGenerationProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      const response = await axios.post('/api/reports/generate', {
        type: selectedReportType,
        playerId: playerId || selectedPlayers[0],
        recipients: emailRecipients,
        format: 'pdf',
        metadata: {
          dateRange,
          ageGroup: 'youth', // Would be determined from player data
        },
      });

      if (response.data.success) {
        setGenerationProgress(100);
        
        toast({
          title: 'Report Generated Successfully',
          description: 'Your report is ready for download',
        });

        // Handle download
        if (response.data.reportUrl) {
          const link = document.createElement('a');
          link.href = response.data.reportUrl;
          link.download = response.data.filename || 'report.pdf';
          link.click();
        }
      }
    } catch (error) {
      console.error('Report generation failed:', error);
      toast({
        title: 'Generation Failed',
        description: 'Unable to generate the report. Please try again.',
        variant: 'destructive',
      });
    } finally {
      clearInterval(progressInterval);
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedPlayers.length === 0) {
      toast({
        title: 'Select players',
        description: 'Please select at least one player for bulk generation',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    try {
      const response = await axios.post('/api/reports/bulk-generate', {
        type: selectedReportType,
        playerIds: selectedPlayers,
        format: 'pdf',
        sendEmail: emailRecipients.length > 0,
        recipients: emailRecipients,
      });

      if (response.data.success) {
        toast({
          title: 'Bulk Generation Complete',
          description: `Generated ${response.data.summary.successful} reports successfully`,
        });
      }
    } catch (error) {
      console.error('Bulk generation failed:', error);
      toast({
        title: 'Bulk Generation Failed',
        description: 'Unable to generate reports. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedReportType('daily_session')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <FileText className="w-8 h-8 text-blue-500" />
              <Badge variant="secondary">Quick</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-semibold mb-1">Generate Today's Report</h3>
            <p className="text-sm text-muted-foreground">
              Create a summary of today's training session
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedReportType('weekly_progress')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <Badge variant="secondary">Popular</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-semibold mb-1">Weekly Progress Report</h3>
            <p className="text-sm text-muted-foreground">
              Analyze performance trends over the past week
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedReportType('parent_update')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 text-orange-500" />
              <Badge variant="secondary">New</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-semibold mb-1">Parent Updates</h3>
            <p className="text-sm text-muted-foreground">
              Share progress with parents and guardians
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Report Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Report</CardTitle>
          <CardDescription>
            Select a report type and configure generation options
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const isSelected = selectedReportType === report.id;
              
              return (
                <div
                  key={report.id}
                  className={`
                    relative p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                  onClick={() => setSelectedReportType(report.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${report.color} text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{report.name}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {report.description}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {report.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-primary" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Configuration Options */}
          {selectedReportType && (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <div className="flex space-x-2">
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    />
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <Select defaultValue="pdf">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="csv">CSV File</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email Recipients (Optional)</Label>
                <Input
                  placeholder="Enter email addresses separated by commas..."
                  onChange={(e) => setEmailRecipients(e.target.value.split(',').map(email => email.trim()))}
                />
                <p className="text-xs text-muted-foreground">
                  Report will be automatically sent to these recipients
                </p>
              </div>

              <div className="flex space-x-3">
                <Button
                  className="flex-1"
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>

                <Button variant="outline" onClick={() => setShowPreview(true)}>
                  <FileDown className="w-4 h-4 mr-2" />
                  Preview
                </Button>

                {emailRecipients.length > 0 && (
                  <Button variant="outline">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Only
                  </Button>
                )}
              </div>

              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Generating report...</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <Progress value={generationProgress} />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle>Recently Generated</CardTitle>
          <CardDescription>
            Your recently generated reports available for download
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Weekly Progress Report</p>
                    <p className="text-sm text-muted-foreground">
                      Generated {format(new Date(), 'PPp')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-xs">
                    1.2 MB
                  </Badge>
                  <Button size="sm" variant="ghost">
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Generation Alert */}
      {selectedPlayers.length > 1 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Bulk Generation:</strong> You have selected {selectedPlayers.length} players.
            Reports will be generated for each player individually.
            <Button
              size="sm"
              variant="link"
              className="ml-2"
              onClick={handleBulkGenerate}
            >
              Generate All
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ReportGenerationDashboard;